import { Accessibility, nullAccessibility } from "../../app/userSlice";
import KeyValuePair from "../../interfaces/keyValuePair";

const fontSizeLookup: KeyValuePair<string, string | null>[] = [
	{ key: "medium", value: "medium" },
	{ key: "large", value: "x-large" },
];

const colorContrastLookup: KeyValuePair<string, string | null>[] = [
	{ key: "blackOnCream", value: "#000000 #fff9cd" },
	{ key: "blackOnLightBlue", value: "#000000 #acd7e6" },
	{ key: "blackOnLightMagenta", value: "#000000 #ed82ed" },
	{ key: "whiteOnBlack", value: "#ffffff #000000" },
	{ key: "yellowOnDarkBlue", value: "#ffff00 #000078" },
];

const tintOverlayLookup: KeyValuePair<string, string | null>[] = [
	{ key: "pink", value: "#ff00ff" },
	{ key: "green", value: "#00ff00" },
	{ key: "blue", value: "#0000ff" },
	{ key: "aqua", value: "#00ffff" },
	{ key: "yellow", value: "#ffff00" },
];

const lineReaderLookup: KeyValuePair<string, string | null>[] = [
	{ key: "enabled", value: "toggle" },
];

function valueToKey(lookup: KeyValuePair<string, string | null>[], value: string | null) {
	return lookup.find((kv) => kv.value?.toLowerCase() === value?.toLowerCase())?.key ?? null;
}

function keyToValue(lookup: KeyValuePair<string, string | null>[], key: string | null) {
	return lookup.find((kv) => kv.key.toLowerCase() === key?.toLowerCase())?.value ?? null;
}

export const axStringToAccessibility = (axString: string | null): Accessibility | null => {
	if (!axString) return null;

	const axStringObj = JSON.parse(axString);
	const accessibility = { ...nullAccessibility };
	accessibility.fontSize = valueToKey(fontSizeLookup, axStringObj?.["font-size"].value);
	accessibility.colorContrast = valueToKey(colorContrastLookup, axStringObj?.["contrast"].value);
	accessibility.tintOverlay = valueToKey(tintOverlayLookup, axStringObj?.["overlay"].value);
	accessibility.lineReader = valueToKey(lineReaderLookup, axStringObj?.["line-reader"].value);
	return accessibility;
};

export const accessibilityToAxString = (accessibility: Accessibility | null): string | null => {
	if (
		!accessibility?.fontSize &&
		!accessibility?.colorContrast &&
		!accessibility?.tintOverlay &&
		!accessibility?.lineReader
	) {
		return null;
	}

	const axStringObj: {
		"font-size": { value: string | null };
		contrast: { value: string | null };
		overlay: { value: string | null };
		"line-reader": { value: string | null };
	} = {
		"font-size": { value: null },
		contrast: { value: null },
		overlay: { value: null },
		"line-reader": { value: null },
	};

	axStringObj["font-size"].value = keyToValue(fontSizeLookup, accessibility.fontSize);
	axStringObj["contrast"].value = keyToValue(colorContrastLookup, accessibility.colorContrast);
	axStringObj["overlay"].value =
		keyToValue(tintOverlayLookup, accessibility.tintOverlay)?.toUpperCase() ?? null;
	axStringObj["line-reader"].value = keyToValue(lineReaderLookup, accessibility.lineReader);

	return JSON.stringify(axStringObj);
};

export function applyAccessibility(accessibility: Accessibility | null) {
	applyFontSize(accessibility?.fontSize ?? null);
	applyColorContrast(accessibility?.colorContrast ?? null);
}

export function resetAccessibility() {
	applyFontSize(null);
	resetColorContrast();
}

export function applyFontSize(fontSize: string | null) {
	const root = document.documentElement;
	switch (fontSize) {
		case "medium":
			root.style.setProperty("--base-font-size", "20px");
			break;
		case "large":
			root.style.setProperty("--base-font-size", "24px");
			break;
		default:
			root.style.setProperty("--base-font-size", "16px");
			break;
	}
}

export function applyColorContrast(contrast: string | null) {
	const value = keyToValue(colorContrastLookup, contrast);
	if (!value) {
		return resetColorContrast();
	}

	const [textColor, backgroundColor] = value.split(" ");

	resetColorContrast();
	const body = document.body;
	body.classList.add("contrast");

	if (contrast) {
		body.classList.add(contrast);
	}

	body.style.color = textColor;
	body.style.borderColor = textColor;
	body.style.fill = textColor;
	body.style.stroke = textColor;
	body.style.backgroundColor = backgroundColor;
	body.style.setProperty("--ccfg", textColor);
	body.style.setProperty("--ccbg", backgroundColor);
}

export function findTint(key: string) {
	const value = keyToValue(tintOverlayLookup, key);
	if (!value) return "transparent";
	return value;
}

function resetColorContrast() {
	const body = document.body;
	const currentClasses = Array.from(body.classList);
	body.classList.remove(...(currentClasses ?? ""));
	body.style.color = "";
	body.style.borderColor = "";
	body.style.backgroundColor = "";
	body.style.fill = "";
	body.style.stroke = "";
	body.style.removeProperty("--ccfg");
	body.style.removeProperty("--ccbg");
}
